import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Trash2, Copy, FileText, X, Check, Search } from 'lucide-react';
import { API } from '@/lib/api';
import { authHeaders, apiError } from '@/admin/AdminContentContext';

const CHUNK = 2 * 1024 * 1024;
export const mediaUrl = (u) => (u && u.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
export const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

export async function uploadFile(file, onProgress) {
  const uploadId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const total = Math.ceil(file.size / CHUNK);
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append('upload_id', uploadId);
    fd.append('filename', file.name);
    fd.append('index', String(i));
    fd.append('chunk', file.slice(i * CHUNK, (i + 1) * CHUNK));
    await axios.post(`${API}/admin/media/chunk`, fd, authHeaders());
    onProgress?.(Math.round(((i + 1) / total) * 100));
  }
  const { data } = await axios.post(`${API}/admin/media/complete`, { upload_id: uploadId, filename: file.name, title: file.name }, authHeaders());
  return data;
}

export function UploadButton({ onDone, className = '' }) {
  const ref = useRef(null);
  const [progress, setProgress] = useState(null);
  const onChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const f of files) {
      const ext = f.name.split('.').pop().toLowerCase();
      const isImg = ['jpg', 'jpeg', 'png', 'webp', 'svg', 'gif'].includes(ext);
      const isFile = ['pdf', 'apk'].includes(ext);
      if (!isImg && !isFile) { toast.error(`نوع الملف غير مسموح: ${f.name}`); continue; }
      if (isImg && f.size > 5 * 1024 * 1024) { toast.error(`الصورة أكبر من 5MB: ${f.name}`); continue; }
      if (isFile && f.size > 50 * 1024 * 1024) { toast.error(`الملف أكبر من 50MB: ${f.name}`); continue; }
      try {
        setProgress(0);
        const m = await uploadFile(f, setProgress);
        toast.success(`تم رفع ${f.name}`);
        onDone?.(m);
      } catch (err) { toast.error(apiError(err, 'فشل الرفع')); } finally { setProgress(null); }
    }
  };
  return (
    <>
      <input ref={ref} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.svg,.gif,.pdf,.apk" className="hidden" onChange={onChange} data-testid="media-file-input" />
      <button type="button" onClick={() => ref.current?.click()} disabled={progress !== null} data-testid="media-upload-btn"
        className={`inline-flex items-center gap-2 rounded-full bg-brand-blue px-4 py-2 text-xs font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60 ${className}`}>
        <Upload className="h-3.5 w-3.5" /> {progress === null ? 'رفع ملف' : `جارٍ الرفع ${progress}%`}
      </button>
    </>
  );
}

export function useMedia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => axios.get(`${API}/admin/media`, authHeaders()).then(({ data }) => setItems(data)).finally(() => setLoading(false));
  useEffect(() => { load().catch(() => toast.error('تعذر تحميل المكتبة')); }, []);
  const remove = async (id) => {
    try { await axios.delete(`${API}/admin/media/${id}`, authHeaders()); setItems((xs) => xs.filter((x) => x.id !== id)); toast.success('تم الحذف'); }
    catch (e) { toast.error(apiError(e)); }
  };
  return { items, loading, load, remove, setItems };
}

export function MediaGrid({ items, onSelect, onRemove, selected }) {
  if (items.length === 0) return <p className="py-14 text-center text-sm text-brand-dim" data-testid="media-empty">لا توجد ملفات بعد. ارفع أول صورة أو ملف.</p>;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="media-grid">
      {items.map((m, i) => (
        <div key={m.id} className={`group relative overflow-hidden rounded-2xl border bg-white transition-all ${selected === m.url ? 'border-brand-blue ring-4 ring-brand-blue/10' : 'border-brand-line'}`} data-testid={`media-item-${i}`}>
          <button type="button" onClick={() => onSelect?.(m)} className="block w-full text-start">
            <div className="grid h-28 place-items-center overflow-hidden bg-brand-paper">
              {m.kind === 'image' ? <img src={mediaUrl(m.url)} alt={m.title} className="h-full w-full object-cover" /> : <FileText className="h-9 w-9 text-brand-steel" />}
            </div>
            <div className="p-2.5">
              <p className="truncate text-xs font-bold text-brand-ink" title={m.original}>{m.title}</p>
              <p className="mt-0.5 font-mono text-[10px] text-brand-dim" dir="ltr">{m.ext.toUpperCase()} · {fmtSize(m.size)}</p>
            </div>
          </button>
          <div className="absolute end-1.5 top-1.5 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            <button type="button" title="نسخ الرابط" onClick={() => { navigator.clipboard.writeText(mediaUrl(m.url)); toast.success('تم نسخ الرابط'); }} data-testid={`media-copy-${i}`} className="grid h-7 w-7 place-items-center rounded-lg bg-white/90 text-brand-steel shadow hover:text-brand-blue"><Copy className="h-3.5 w-3.5" /></button>
            {onRemove && <button type="button" title="حذف" onClick={() => onRemove(m.id)} data-testid={`media-delete-${i}`} className="grid h-7 w-7 place-items-center rounded-lg bg-white/90 text-brand-steel shadow hover:text-brand-crimson"><Trash2 className="h-3.5 w-3.5" /></button>}
          </div>
          {selected === m.url && <span className="absolute start-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-full bg-brand-blue text-white"><Check className="h-3.5 w-3.5" /></span>}
        </div>
      ))}
    </div>
  );
}

export function MediaPickerModal({ value, onPick, onClose, accept }) {
  const { items, loading, remove, setItems } = useMedia();
  const [q, setQ] = useState('');
  const shown = items.filter((m) => (!accept || m.kind === accept) && (!q || m.title.toLowerCase().includes(q.toLowerCase())));
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-brand-ink/50 p-4 backdrop-blur-sm" data-testid="media-picker-modal" onClick={onClose}>
      <div className="flex max-h-[85vh] w-full max-w-4xl flex-col rounded-3xl bg-brand-paper shadow-2xl" onClick={(e) => e.stopPropagation()} dir="rtl">
        <div className="flex flex-wrap items-center gap-3 border-b border-brand-line px-6 py-4">
          <h3 className="font-display text-lg font-extrabold text-brand-ink">مكتبة الوسائط</h3>
          <div className="relative ms-auto">
            <Search className="absolute start-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-brand-dim" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث…" data-testid="media-picker-search" className="w-48 rounded-full border border-brand-line bg-white py-2 ps-9 pe-3 text-xs outline-none focus:border-brand-blue" />
          </div>
          <UploadButton onDone={(m) => { setItems((xs) => [m, ...xs]); onPick(m); }} />
          <button type="button" onClick={onClose} data-testid="media-picker-close" className="grid h-9 w-9 place-items-center rounded-full border border-brand-line bg-white text-brand-steel hover:text-brand-crimson"><X className="h-4 w-4" /></button>
        </div>
        <div className="overflow-y-auto p-6" data-lenis-prevent>
          {loading ? <p className="py-14 text-center text-sm text-brand-dim">جارٍ التحميل…</p> : <MediaGrid items={shown} selected={value} onSelect={onPick} onRemove={remove} />}
        </div>
      </div>
    </div>
  );
}
